import { useCallback, useEffect, useState } from 'react';
import { TERMINAL_STATUSES, dayIn, nextDay } from '@delivery/shared';
import { CONTACT_ERROR_TAGS, CONTACT_ERROR_TAG_KEYS } from '@delivery/shared';
import { api, ApiFailure } from '../lib/api';
import { cachedGet } from '../lib/cache';
import { currentOrder } from '../lib/currentOrder';
import { money } from '../lib/money';
import { getPosition } from '../lib/native';
import { launch, navPreference, openNavigation, type NavOption } from '../lib/navigate';
import { outbox } from '../lib/outbox';
import { PhotoPicker, type UploadedPhoto } from '../components/PhotoPicker';
import { CompleteSheet } from '../components/CompleteSheet';
import type { Task, TasksResponse } from '../lib/types';
import { Banknote, Camera, MapPin, Navigation, Phone, Store, type LucideIcon } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

/**
 * 订单详情 —— **列表和地图进的是同一个**。
 *
 * ★ 在这之前,司机想对一单做点什么,路径取决于他从哪进来:
 *   今日列表里点一下只是「设为当前任务」,取货得绕到「待取货」那个二级页,
 *   地图上点图钉弹的卡片只有导航按钮。同一件事三个入口、三种能做的事,
 *   而他要的只是「这一单现在该干嘛」。
 *
 * ★ 里面有什么操作**由状态决定**,不由入口决定:
 *
 *     未领取 → 领取     已派单 → 接单
 *     已接单 → 取货     已取货/配送中 → 完成
 *
 *   这样他不需要知道自己是从列表还是地图进来的 —— 打开就是能干的那件事。
 *
 * ★ 信息分**上下两段**:上面发货方、下面收货方,各自都有姓名、地址、电话。
 *   原来只显示收件人 —— 而司机到了店里找不到人的时候,
 *   唯一的办法是打给调度、让调度再打给商家。
 */

interface PoolRow {
  id: string;
  orderNo: string;
  /*
   * ★ 池子现在**给**姓名和完整地址(用户要求:司机得靠它判断值不值得领),
   *   只有电话是打码的。所以这一页不该再显示「领取后可见」那套占位 ——
   *   数据明明在手上,却告诉他「领了才看得到」。
   */
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  pickup?: { name: string; address: string; lat: number | null; lng: number | null };
  zone: string | null;
  pickupPoint: string | null;
  packageCount: number;
  weightGram: number | null;
  codAmount: string;
  /** 要代收但金额待定 —— 界面写「联系商家确认」,不写 0 */
  codPending?: boolean;
  /** 冻品、易碎…… 池子里也给,司机决定领不领要看这个 */
  specialItems?: Array<{ code: string; label: string; icon: string | null }>;
  payout: string;
  currency: string;
  lat?: number | null;
  lng?: number | null;
}

/** 详情里当前能做的那一件事 */
type Action =
  | { kind: 'GRAB'; label: '领取这一单' }
  | { kind: 'ACCEPT'; label: '接单' }
  | { kind: 'PICKUP'; label: '确认取货' }
  | { kind: 'COMPLETE'; label: '送达 / 未送达' | '送达' }
  | { kind: 'NONE'; label: string };

/**
 * 错误标记的文案。
 *
 * ★ 每一条都写「标了之后会怎样」—— 只给三个词的话,
 *   司机不知道该选哪个,更不知道值不值得点这一下。
 */
const MARK_LABEL: Record<string, string> = {
  PHONE: '电话有问题',
  ADDRESS: '地址有问题',
  BOTH: '电话和地址都有问题',
};

/*
 * ★ 用共享那份,不在这里另写一遍。
 *
 *   这里原来自己写了三行,其中「电话打不通」和共享定义的
 *   「电话号码不对」说的**不是同一件事**:打不通可能只是对方在忙,
 *   而这个标记会跟着这个人以后每一单(客服看到会不敢发单)。
 *   司机看到的措辞和客服看到的不一样,谁也不会发现 ——
 *   这正是 shared/order/labels.ts 顶上那句警告说的情况,
 *   而这个文件就是那句话的反例。
 */
const MARK_OPTIONS = CONTACT_ERROR_TAG_KEYS.map((key) => ({
  key,
  label: CONTACT_ERROR_TAGS[key].label,
  hint: CONTACT_ERROR_TAGS[key].description,
  /** 什么情况不该标 —— 比「该标什么」更能防住误标 */
  notThis: CONTACT_ERROR_TAGS[key].notThis,
}));

function actionFor(status: string, isMine: boolean, deliverOnly: boolean): Action {
  if (!isMine) return { kind: 'GRAB', label: '领取这一单' };
  switch (status) {
    case 'ASSIGNED':
      return { kind: 'ACCEPT', label: '接单' };
    case 'ACCEPTED':
      return { kind: 'PICKUP', label: '确认取货' };
    case 'PICKED_UP':
    case 'IN_TRANSIT':
    case 'FAILED':
      /*
       * ★ 「只显示送达」开着时,按钮上就只有「送达」——
       *   点一下直接进送达面板,少一次「送达还是未送达」的选择。
       *   送不到的路挪到面板底部一行小字(见 CompleteSheet)。
       */
      return { kind: 'COMPLETE', label: deliverOnly ? '送达' : '送达 / 未送达' };
    case 'DELIVERED':
      return { kind: 'NONE', label: '已送达' };
    case 'RETURNED':
      return { kind: 'NONE', label: '已退回' };
    case 'CANCELLED':
      return { kind: 'NONE', label: '已取消' };
    default:
      return { kind: 'NONE', label: '暂无可做的操作' };
  }
}

/**
 * 期望送达日期的人话写法:今天 / 明天 / `09-25`;过了的写「已过」。
 *
 * ★ 不写日期数字:司机一眼要判断的是「这单是不是今天必须送掉」,
 *   而 `09-23` 要他自己先想今天几号。
 */
function expectedDayText(at: string, tz: string, status?: string): string {
  const today = dayIn(tz);
  const day = dayIn(tz, new Date(at));
  // 送达 / 退回 / 取消了的单不再说「已过」—— 那是给还没做完的单的提醒
  const done = !!status && (TERMINAL_STATUSES as readonly string[]).includes(status);
  if (day < today && !done) return `${day.slice(5)}(已过)`;
  if (day === today) return '今天';
  if (day === nextDay(today)) return '明天';
  return day.slice(5);
}

export function OrderDetail({
  orderId,
  tz,
  onClose,
  onChanged,
  deliverOnly = false,
  confirmPrechecked = false,
  whatsapp = false,
}: {
  orderId: string;
  /**
   * 店里的时区 —— 「今天/明天」按它算,不按手机。
   *
   * ★ 司机出个境、手机自动改了时区,零点就跟着偏几个小时,
   *   而单子还是照店里的日子在走(和列表那一屏同一个理由)。
   */
  tz: string;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  /** 只显示「送达」——见 CompleteSheet 里的说明 */
  deliverOnly?: boolean;
  /** 代收、特殊物品这些确认项默认勾上 */
  confirmPrechecked?: boolean;
  /** 电话旁边显不显示 WhatsApp 按钮 */
  whatsapp?: boolean;
}) {
  const [task, setTask] = useState<Task | null>(null);
  const [pool, setPool] = useState<PoolRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [completing, setCompleting] = useState(false);
  const [navChoices, setNavChoices] = useState<NavOption[] | null>(null);
  /** 这个收件人有没有被标记过「电话/地址有问题」*/
  const [mark, setMark] = useState<{
    errorTag: string | null;
    errorNote: string | null;
  } | null>(null);
  const [marking, setMarking] = useState(false);

  /*
   * 「我的备注」—— 司机自己记的那几条,以及正在输入的那一条。
   *
   * ★ 单独拉一次,不塞进任务列表:那个接口是离线缓存的数据源,
   *   而备注是进了详情才看的东西,没必要让每次同步都背着它。
   */
  const [savingNote, setSavingNote] = useState(false);
  /*
   * ★ 这个框**预填他已经写过的那条**,存下去是整条换掉。
   *
   *   用户原话:「他也可以修改自己这个备注」。
   *   只能追加的话,他想改掉写错的半句只能再写一行
   *   「上面那句不算」—— 然后两行互相矛盾,而看的人不知道信哪句。
   *
   * ★ 刚存下的那句先记在本地(justSaved),不等任务列表刷新:
   *   服务端已经落库了,但那份要等下一次同步才回到手机上 ——
   *   中间这几秒他会看到自己刚打的字消失,而在路上,
   *   那会让他以为没存上、再打一遍。
   */
  const [justSaved, setJustSaved] = useState<string | null>(null);
  const myNote = justSaved ?? task?.driverNote ?? null;
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const draft = noteDraft ?? myNote ?? '';

  const addMyNote = useCallback(async () => {
    const text = draft.trim();
    if (savingNote) return;
    setSavingNote(true);
    try {
      await api(`/driver/orders/${orderId}/note`, { method: 'PATCH', body: { note: text } });
      setJustSaved(text);
      setNoteDraft(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '这条备注没存上');
    } finally {
      setSavingNote(false);
    }
  }, [draft, savingNote, orderId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      /*
       * ★ 先在自己的任务里找,找不到再去池子里找。
       *   顺序不能反 —— 刚领完的那一瞬间两边都可能有它,
       *   而「我的」那一份信息才是全的(池子里的地址是脱敏过的)。
       */
      /*
       * ★ 带上 includeFuture=1 —— 那是「今天的 + 以后的」,一份全的。
       *
       *   不带的话只有今天的单:**非当日达**的单在这里找不到,
       *   落到下面第三档按管理端形状读,那一份没有 proof / customerId ——
       *   点「确认取货」会在读 `task.proof.pickup` 时直接抛错,什么都没发出去。
       *   用户报的:「没有用批量取货的时候,直接进订单里面点取货,还是会一直处于取货状态」。
       *   (批量取货那一页一直是跟着「非当日达」开关带这个参数的,所以那边是好的。)
       *
       * ★ 离线时退回 `/driver/tasks` 那份缓存 —— 少几单也比整页报错强。
       */
      const r = await cachedGet<TasksResponse>('/driver/tasks?includeFuture=1', '/driver/tasks');
      const mine = r.data.tasks.find((t) => t.id === orderId);
      if (mine) {
        setTask(mine);
        setPool(null);
      } else {
        /*
         * ★ 按 id 直接问池子,不是「把列表拉回来在里面翻」。
         *
         *   列表是有上限的(策略里的 poolMaxVisible)。从地图上点开的
         *   那一单十有八九不在那一批里 —— 于是详情页说
         *   「这一单不在你名下,也不在可领取的池子里,多半是刚被别人领走」,
         *   而它**就在池子里,而且没人领**。用户报的就是这个。
         */
        const inPool = await api<PoolRow | null>(`/driver/pool/${orderId}`).catch(() => null);
        if (inPool) {
          setPool(inPool);
          setTask(null);
        } else {
          /*
           * ★ 第三档:直接按单号去查。
           *
           *   前两处找的都是「今天的」——`/driver/tasks` 只回当天动过的单,
           *   池子里只有还没人领的。而从「已取消」那一栏点进来的
           *   多半是几天前的单,两处都找不到 ——
           *   表现是点开一个空白页,像是详情页坏了。
           *   司机对自己的单本来就有读权限,直接查就是了。
           */
          /*
           * ★ 这一档给的是「已完成/已取消的历史单」—— 它们不在今天的任务里,
           *   也不在池子里。别人的单在这里读不到:司机的数据范围是 SELF,
           *   服务端直接回 404(试过了),所以不必在这儿再判一次归属 ——
           *   前端判归属反而会给人一种「这道边界在前端」的错觉。
           */
          /*
           * ★ 这个接口回的是**管理端形状**:取货点在 `pickupPoint` 里,
           *   而司机端的 Task 用的是 `pickup`。
           *
           *   原来是直接 `as Task` —— 那是在骗类型。界面上那一排
           *   `task?.pickup.name` 会读到 undefined 然后在**渲染时**抛
           *   TypeError,而司机端没有 error boundary:React 把整棵树卸载掉,
           *   屏幕上**一片空白**,连返回按钮都没有。
           *   用户报的原话:「完成订单点进去查看详情页面会没有任何内容」。
           *
           *   所以在这儿补齐,而不是让每个用到 pickup 的地方各自防一手 ——
           *   形状对不上是**边界上的问题**,在边界上解决。
           */
          const raw = await api<Record<string, unknown>>(`/orders/${orderId}`);
          const pp = raw['pickupPoint'] as {
            id?: string;
            name?: string;
            address?: string;
          } | null;
          const customer = raw['customer'] as { id?: string; publicId?: string } | null | undefined;
          const one = {
            ...(raw as unknown as Task),
            /*
             * ★ 这一份没有凭证要求和商家 id(管理端形状)。补上兜底,
             *   不然点「确认取货」会在读 `task.proof.pickup` 时抛错 ——
             *   而那个错误又被紧跟着的 load() 清掉,司机看到的是「点了没反应」。
             *   真要拍照的话服务端会拒,错误照样显示出来。
             */
            proof: (raw['proof'] as Task['proof'] | undefined) ?? {
              pickup: { required: false, mode: 'PER_ORDER', minPhotos: 0, maxPhotos: 5 },
              delivery: { required: false, mode: 'PER_ORDER', minPhotos: 0, maxPhotos: 5 },
            },
            customerId:
              (raw['customerId'] as string | undefined) ??
              customer?.publicId ??
              customer?.id ??
              null,
            pickup:
              (raw['pickup'] as Task['pickup'] | undefined) ??
              ({
                id: pp?.id ?? null,
                name: pp?.name ?? '',
                address: pp?.address ?? (raw['pickupAddress'] as string) ?? '',
                contactName: null,
                contactPhone: null,
                lat: null,
                lng: null,
              } satisfies Task['pickup']),
          } as Task;
          setTask(one);
          setPool(null);
        }
      }
      setError(null);
      /*
       * ★ 顺带查一下这个收件人有没有被标记过。
       *   出发前就知道「这个号打不通,直接按地址去」——
       *   而这条信息现在只有上一个跑过这一单的司机知道。
       */
      void api<{ errorTag: string | null; errorNote: string | null } | null>(
        `/driver/orders/${orderId}/contact-mark`,
      )
        .then((m) => setMark(m))
        .catch(() => undefined);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isMine = task != null;
  const status = task?.status ?? 'PENDING';
  const action = actionFor(status, isMine, deliverOnly);

  /** 标记 / 撤销标记 —— 标记落在**联系人**上,下一单才看得到 */
  async function submitMark(tag: 'PHONE' | 'ADDRESS' | 'BOTH' | null) {
    setMarking(false);
    try {
      await api(`/driver/orders/${orderId}/contact-error`, {
        method: 'POST',
        body: { errorTag: tag },
      });
      setMark(tag ? { errorTag: tag, errorNote: null } : null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '标记失败');
    }
  }

  /**
   * 导航过去。
   *
   * ★ 点导航的那一刻,就等于他宣布了「我现在要去这一单」——
   *   所以顺手把它记成当前那一单。司机的原话:
   *   「当点击某个订单的导航的时候就变成这一单,这样方便快速找到正在去的那一单」。
   *
   *   不用他再去列表里手动点一下 ▶ —— 那一步是纯粹的重复劳动,
   *   而且他这会儿正要发动车子。
   */
  function navigateTo(lat: number | null, lng: number | null, label: string) {
    if (lat == null || lng == null) return;
    currentOrder.set(orderId);
    const opts = openNavigation(lat, lng, label);
    if (opts) setNavChoices(opts);
  }

  async function run() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (action.kind === 'GRAB') {
        await api(`/orders/${orderId}/transitions`, {
          method: 'POST',
          body: { action: 'GRAB', idempotencyKey: `grab-${orderId}-${Date.now()}` },
        });
      } else if (action.kind === 'ACCEPT') {
        await api(`/orders/${orderId}/transitions`, {
          method: 'POST',
          body: { action: 'ACCEPT', idempotencyKey: `acc-${orderId}-${Date.now()}` },
        });
      } else if (action.kind === 'PICKUP') {
        await doPickup();
        return; // doPickup 自己负责刷新和错误
      } else if (action.kind === 'COMPLETE') {
        // 送达/未送达有一整套要填的东西(签收人、失败原因、货物去向),
        // 那一套已经在 CompleteSheet 里,不在这儿重写一遍
        setCompleting(true);
        setBusy(false);
        return;
      }
      await onChanged();
      await load();
    } catch (e) {
      // 领取失败最常见的原因是「刚被别人领走了」—— 刷新让他看到那一单没了
      await load();
      /*
       * ★ 错误要在 load() **之后**再设。load() 成功时会清掉错误 ——
       *   原来先设后刷,那句话一闪就没了,司机看到的是「点了没反应」。
       */
      setError(e instanceof ApiFailure ? e.message : e instanceof Error ? e.message : '操作失败');
    } finally {
      setBusy(false);
    }
  }

  /**
   * 取货。
   *
   * ★ 和「待取货」那一页走的是同一条路:照片进队列、等第一次同步的结果。
   *   司机**站在店里**等结果 —— 成了就走人,没成他得当场处理
   *   (开定位、走近一点、找店员)。这时候把失败咽下去,他会以为成了。
   */
  async function doPickup() {
    if (!task) return;
    const needPhoto = task.proof.pickup.required;
    if (needPhoto && photos.length < task.proof.pickup.minPhotos) {
      setError(`这一单需要 ${task.proof.pickup.minPhotos} 张商品照才能取货`);
      setBusy(false);
      return;
    }

    const geo = await getPosition();
    const queuedId = await outbox.enqueue({
      orderIds: [task.id],
      action: 'PICKUP',
      endpoint: '/orders/batch-transition',
      body: {
        action: 'PICKUP',
        orderIds: [task.id],
        batchKey: '',
        ...(geo ? { geo } : {}),
        ...(task.pickup?.id ? { payload: { pickupPointId: task.pickup.id } } : {}),
      },
      photos: task.customerId
        ? photos.map((p) => ({
            slot: `customer:${task.customerId}`,
            purpose: 'PICKUP_PHOTO',
            blob: p.blob,
          }))
        : [],
    });

    const left = await outbox.settle(queuedId);
    if (left?.error) {
      setError(left.error.message);
      await outbox.discard(queuedId);
      setBusy(false);
      return;
    }
    /*
     * ★ 还在队列里(没 error,但这条记录还在)—— 要**说出来**,不能当成功。
     *
     *   会走到这儿的情况:同一单前面还压着一条没发出去的操作,
     *   或者那一条在等重试的间隔。以前这里直接往下走,
     *   界面刷一下什么都没变 —— 司机站在店里点了一下,毫无反应。
     *   它确实会自动重发,但这句话必须由屏幕说,而不是让他自己猜。
     */
    if (left) {
      setError(
        '这一下还在排队发送(网络慢,或者这一单前面还有没发出去的操作)—— 会自动重发,先别重复点',
      );
      setBusy(false);
      return;
    }
    setPhotos([]);
    await onChanged();
    await load();
    setBusy(false);
  }

  if (loading) {
    return <div className="grid h-64 place-items-center text-slate-400">加载中…</div>;
  }

  if (!task && !pool) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">这一单不在你名下,也不在可领取的池子里。</p>
        <p className="mt-1 text-sm text-slate-400">多半是刚被别人领走,或者已经结束了。</p>
        <button onClick={onClose} className="mt-4 text-brand-600 dark:text-brand-500">
          返回
        </button>
      </div>
    );
  }

  /*
   * ★ 这一屏的数据有**三种**来源,不是两种:
   *   ① 今天的任务(/driver/tasks)—— 完整的 Task
   *   ② 可领取的池子(/driver/pool/:id)—— 脱敏过的 PoolRow
   *   ③ 往天的历史单(/orders/:id)—— **管理端形状**,字段不全
   *
   *   下面这一整段原来写的是 `task?.X ?? pool!.X`,而那个写法假设
   *   「task 在,task.X 就一定在」。第 ③ 种打破了这个假设:
   *   缺字段时 `??` 会落到 `pool!`,而那时 pool 是 null —— 当场抛,
   *   而抛在渲染里就是**整屏空白**(用户报的:「详情没有任何内容」)。
   *
   *   所以一律 `pool?.` 加一个兜底值。少显示一个数字,
   *   远好过让他连返回按钮都找不到。
   */
  const orderNo = task?.orderNo ?? pool?.orderNo ?? '';

  /*
   * ★ 这一单要收钱、或者车上有特殊物品时,整屏框一圈会呼吸的边。
   *   司机是边走边看的,眼睛落在屏幕中间 ——
   *   而那两块提醒可能已经滚上去了。
   */
  const needsAlertFrame =
    Number(task?.codAmount ?? pool?.codAmount ?? 0) > 0 ||
    (task?.codPending ?? pool?.codPending) === true ||
    (task?.specialItems ?? pool?.specialItems ?? []).length > 0;

  return (
    <div className="pb-40">
      {needsAlertFrame && (
        /*
          ★ 三层叠出来的雾。空的 <i> 没有语义,纯装饰 ——
            读屏软件不该念到它们(整块 aria-hidden)。
            提醒的**内容**在下面那两块里,那才是要被念出来的。
        */
        <div aria-hidden className="alert-frame">
          {/*
            三层各司其职,顺序不能换(见 index.css 里的说明):
              i    —— 糊(filter)
              span —— 切出圆角环(mask)
              u    —— 转着的锥形渐变,「流动」的来源
          */}
          <i className="glow">
            <span>
              <u />
            </span>
          </i>
          <i className="haze">
            <span>
              <u />
            </span>
          </i>
        </div>
      )}
      <div className="flex items-center gap-2 px-4 py-3">
        <button onClick={onClose} aria-label="返回" className="-ml-1 text-2xl text-slate-400">
          ‹
        </button>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">订单详情</h2>
        <span className="ml-auto font-mono text-xs text-slate-500">{orderNo}</span>
      </div>

      {error && (
        <div
          role="alert"
          className="mx-4 mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700
                     dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <div className="space-y-3 px-4">
        {/*
          ★ 上下两段:发货在上、收货在下,和货物移动的方向一致。
            两段都给姓名、地址、电话 —— 缺任何一个,他都得打给调度问。
        */}
        <Party
          role="发货"
          icon={Store}
          /*
           * ★ 每一段都要 `?.`。`task?.pickup.name` 只兜住了 task ——
           *   task 在、pickup 是 undefined 时照样抛,而抛在渲染里
           *   等于整个 app 变空白。上面那个归一化已经保证 pickup 一定在,
           *   这里再兜一层:以后多一条取数的路,不至于又白屏一次。
           */
          name={task?.pickup?.name || pool?.pickup?.name || pool?.pickupPoint || '—'}
          contactName={task?.pickup?.contactName ?? null}
          phone={task?.pickup?.contactPhone ?? null}
          address={task?.pickup?.address || pool?.pickup?.address || '—'}
          lat={task?.pickup?.lat ?? pool?.pickup?.lat ?? null}
          lng={task?.pickup?.lng ?? pool?.pickup?.lng ?? null}
          onNavigate={navigateTo}
          note={null}
          whatsapp={whatsapp}
        />

        <div className="text-center text-slate-300 dark:text-slate-600">↓</div>

        <Party
          role="收货"
          icon={MapPin}
          name={task?.receiverName ?? pool?.receiverName ?? '—'}
          contactName={null}
          phone={task?.receiverPhone ?? pool?.receiverPhone ?? null}
          address={task?.receiverAddress ?? pool?.receiverAddress ?? '—'}
          /*
           * ★ 收货坐标始终用 `receiverLat/Lng`,不要用 `lat/lng`。
           *   后者是「当前该去的那个点」,取货阶段它指的是取货点 ——
           *   于是这一段拿到 null,导航按钮直接不出现,
           *   而司机在取货前最想知道的正是「拿完往哪送」。
           */
          lat={task?.receiverLat ?? pool?.lat ?? null}
          lng={task?.receiverLng ?? pool?.lng ?? null}
          onNavigate={navigateTo}
          /*
           * ★ 只有**电话**是打码的,而且要说清是哪一样没给全 ——
           *   原来整块写「领取后显示完整信息」,而姓名和地址明明就在上面。
           *   笼统的一句话会让他以为地址也不全,于是不敢照着走。
           */
          note={isMine ? null : '电话领取后显示完整号码'}
          primary
          whatsapp={whatsapp}
        />

        {/*
          ★ 已经被标过的,在**出发之前**就摆出来。
            这条信息现在只有上一个跑过这一单的司机知道 ——
            而下一个人多半会照着同一个号码再打十遍。
        */}
        {mark?.errorTag && (
          <div
            className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800
                          dark:bg-amber-950/40 dark:text-amber-300"
          >
            以前有人标过:{MARK_LABEL[mark.errorTag] ?? mark.errorTag}
            {mark.errorNote ? `(${mark.errorNote})` : ''}
          </div>
        )}

        {/*
          ★ 「这一单有什么要当心的」就摆在收货信息**正下方**。
            这是他看完地址准备出发的那一眼 —— 再往下就是订单信息、
            拍照区,他不会滚回来。
        */}
        <Alerts
          codAmount={task?.codAmount ?? pool?.codAmount ?? '0'}
          codPending={task?.codPending ?? pool?.codPending ?? false}
          currency={task?.currency ?? pool?.currency ?? ''}
          specialItems={task?.specialItems ?? pool?.specialItems ?? []}
        />

        {/*
          ★ 只有自己手上这一单才给标 —— 池子里的单他还没接,
            而标记会影响之后每一次建单。
        */}
        {isMine && (
          <button
            onClick={() => setMarking(true)}
            className="w-full rounded-xl border border-slate-300 py-2.5 text-sm text-slate-600
                       dark:border-slate-600 dark:text-slate-300"
          >
            这个收件人有问题
          </button>
        )}

        <section
          aria-label="订单信息"
          className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700"
        >
          <Row label="状态" value={STATUS_TEXT[status] ?? status} />
          {/*
            ★ 期望送达日期给司机看。
              他在详情里要判断的是「这单是今天必须送掉,还是明天的活」——
              而列表默认只列当天的,单独点进来的时候更需要这一行说清楚。
          */}
          {(task?.expectedDeliveryAt ?? null) && (
            <Row label="期望送达" value={expectedDayText(task!.expectedDeliveryAt!, tz, task!.status)} />
          )}
          <Row label="件数" value={`${task?.packageCount ?? pool?.packageCount ?? 1} 件`} />
          {(task?.weightGram ?? pool?.weightGram) != null && (
            <Row
              label="重量"
              value={`${((task?.weightGram ?? pool?.weightGram)! / 1000).toFixed(1)} kg`}
            />
          )}
          {(Number(task?.codAmount ?? pool?.codAmount ?? 0) > 0 ||
            (task?.codPending ?? pool?.codPending)) && (
            <Row
              label="代收货款"
              value={
                (task?.codPending ?? pool?.codPending)
                  ? '联系商家确认'
                  : money(
                      task?.codAmount ?? pool?.codAmount ?? 0,
                      task?.currency ?? pool?.currency ?? '',
                    )
              }
              strong
            />
          )}
          <Row
            label="我的酬劳"
            value={money(task?.payout ?? pool?.payout ?? 0, task?.currency ?? pool?.currency ?? '')}
          />
          {task?.zone && <Row label="区域" value={`${task.zone.code} ${task.zone.name}`} />}
          {/*
            ★ 订单的备注**只有一条**(用户原话:「订单里面的备注就应该
              只有一个字段」)。收件人身上那句长期成立的交代(门禁密码、
              走侧门)在建单时就并进去了,所以这里不再有第二行「常驻备注」。

            ★ 他自己记的那条排在**上面**,而且单独一条。
              用户原话:「司机加的那个是单独的,主要是司机看,
              放到正常备注的上一行来显示」——
              他记的多半是「门口有狗」这类下次要用的事,
              混进客服那句里他分不清哪句是这次特别说的。
          */}
          {myNote && <Row label="我的备注" value={myNote} />}
          {task?.receiverNote && <Row label="备注" value={task.receiverNote} />}
        </section>

        {/*
          ★ 司机只能**加**,改不了上面那条订单备注。
 
            这不是界面上藏起来就行 —— 服务端也拦着:改订单要 order:update,
            而司机没有这个权限。界面这边只是不给他一个会失败的入口。
 
          ★ 加的那句同时落两处:`order.driver_note` 这一列(**追加**拼上去,
            所以他也改不掉自己上一句),以及 order_event(NOTE_ADDED,
            带操作者和时间)。列是**现状**,给列表、详情、司机端显示用;
            事件是**历史**,追责时要看的是它。
            只有事件的话,想显示就得每行再查一遍事件表 —— 那是热接口上的 N+1,
            而事实上以前干脆哪儿都没显示。
        */}
        <section className="mt-3 rounded-2xl bg-white p-4 dark:bg-slate-800">
          {/*
            ★ 这儿只留输入框,不再把他记过的几条再列一遍 ——
              同一屏上面那行「我的备注」已经是全文了。
              同样的内容摆两处,改一处忘一处是迟早的事。
          */}
          <p className="mb-2 text-sm font-medium text-slate-900 dark:text-slate-100">我的备注</p>
          <div className="flex gap-2">
            <input
              aria-label="我的备注"
              value={draft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="记一句给自己和后台看的"
              className="h-11 flex-1 rounded-xl border border-slate-300 px-3 text-base
                         dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
            <button
              onClick={() => void addMyNote()}
              disabled={savingNote || draft.trim() === (myNote ?? '').trim()}
              className="h-11 shrink-0 rounded-xl bg-brand-500 px-4 font-medium text-white
                         disabled:opacity-40"
            >
              {savingNote ? '存着…' : '保存'}
            </button>
          </div>
        </section>
        {/* 取货要拍照的话,入口就在按钮上面 —— 别让他去别的页面找 */}
        {action.kind === 'PICKUP' && task?.proof?.pickup?.required && (
          <section
            aria-label="取货照片"
            className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
          >
            <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">
              <InlineIcon icon={Camera} /> {task.proof.pickup.hint ?? '请拍到商品外包装和件数'}
            </p>
            <PhotoPicker
              purpose="PICKUP_PHOTO"
              max={Math.max(1, task.proof.pickup.minPhotos)}
              photos={photos}
              onChange={setPhotos}
            />
          </section>
        )}
      </div>

      {/* ★ 主操作固定在底部,拇指够得到 —— 司机是单手用的 */}
      <div
        className="fixed inset-x-0 bottom-16 z-20 border-t border-slate-200 bg-white/95 p-3
                      backdrop-blur dark:border-slate-700 dark:bg-slate-900/95"
      >
        <button
          onClick={() => void run()}
          disabled={busy || action.kind === 'NONE'}
          className="h-13 w-full rounded-xl bg-brand-600 py-3 text-lg font-medium text-white
                     disabled:opacity-40"
        >
          {busy ? '处理中…' : action.label}
        </button>
      </div>

      {completing && task && (
        <CompleteSheet
          task={task}
          deliverOnly={deliverOnly}
          confirmPrechecked={confirmPrechecked}
          onClose={() => setCompleting(false)}
          onDone={async () => {
            setCompleting(false);
            await onChanged();
            /*
             * ★ 做完就**退出这一屏**,回到他来的地方(列表或地图)。
             *
             *   用户原话:「订单点击送达完成后自动关闭这个订单详情吧,
             *   回到之前的页面(列表/地图之类的)」。
             *
             *   这一单已经结束了,留在详情上没有下一步可做 ——
             *   而他此刻要的是「下一单在哪」。让他自己点一下返回,
             *   是每送一单都要做一次的多余动作。
             *
             * ★ 不再 `load()`:这一屏马上就不在了,再拉一次数据是白跑,
             *   而且那个请求回来时组件已经卸载(会在控制台里留一串
             *   setState-on-unmounted 的噪音)。
             *   列表那边由上面 `onChanged()` 负责刷新。
             */
            onClose();
          }}
        />
      )}

      {marking && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/40"
          onClick={() => setMarking(false)}
        >
          <div
            role="dialog"
            aria-label="标记收件人问题"
            className="w-full rounded-t-2xl bg-white p-4 dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-medium text-slate-900 dark:text-slate-100">
              哪里不对?
            </div>
            {/*
              ★ 说清楚标了会怎样 —— 否则这一下点不点全看心情,
                而这条信息对下一个跑这一单的人价值最大。
            */}
            <p className="mt-1 text-xs text-slate-400">标了之后,客服下次给这个人建单时会当场看到</p>

            <div className="mt-3 space-y-2">
              {MARK_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  onClick={() => void submitMark(o.key)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-left
                             dark:border-slate-600"
                >
                  <div className="text-slate-900 dark:text-slate-100">{o.label}</div>
                  <div className="text-xs text-slate-400">{o.hint}</div>
                  {/* ★ 「什么情况不该标」写在选项里 —— 标错的代价
                         落在这个人以后的每一单上,而司机在门口看不到那个代价 */}
                  {o.notThis && (
                    <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
                      {o.notThis}
                    </div>
                  )}
                </button>
              ))}
              {mark?.errorTag && (
                <button
                  onClick={() => void submitMark(null)}
                  className="w-full rounded-xl px-3 py-2 text-sm text-slate-500"
                >
                  撤销标记(核对过了,没问题)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {navChoices && <NavSheet options={navChoices} onClose={() => setNavChoices(null)} />}
    </div>
  );
}

/** 一方的联系信息:名字、地址、电话、导航 */
function Party({
  role,
  icon,
  name,
  contactName,
  phone,
  address,
  lat,
  lng,
  onNavigate,
  note,
  primary,
  whatsapp,
}: {
  role: string;
  icon: LucideIcon;
  name: string;
  contactName: string | null;
  phone: string | null;
  address: string;
  lat: number | null;
  lng: number | null;
  onNavigate: (lat: number | null, lng: number | null, label: string) => void;
  /** 这一块里有什么是没给全的 —— 没有就传 null */
  note: string | null;
  /** 这一侧的导航是不是「主要那一个」—— 收货侧高亮 */
  primary?: boolean;
  /** 显不显示 WhatsApp 按钮(租户开关) */
  whatsapp?: boolean;
}) {
  return (
    <section
      aria-label={role}
      className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
    >
      <div className="mb-1 flex items-center gap-2">
        <InlineIcon icon={icon} />
        <span className="text-xs text-slate-500">{role}</span>
        {/* 有哪一样没给全就说清是哪一样,别笼统地说「领取后可见」 */}
        <span className="ml-auto text-xs text-slate-400">{note}</span>
      </div>
      <div className="font-medium text-slate-900 dark:text-slate-100">{name}</div>
      {contactName && (
        <div className="text-sm text-slate-600 dark:text-slate-400">{contactName}</div>
      )}
      <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">{address}</p>

      <div className="mt-2 flex flex-wrap gap-2">
        {phone && (
          <>
            <a
              href={`tel:${phone}`}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm
                         dark:border-slate-600 dark:text-slate-300"
            >
              <InlineIcon icon={Phone} /> {phone}
            </a>
            {/*
              ★ WhatsApp 按钮跟着开关走,默认**不显示**。
                它和「打电话」长得一模一样并排摆着,而路上真正要按的是打电话 ——
                多一个同样大小的按钮,只是多一次按错的机会。
                要用的租户在设置里打开就行。
            */}
            {whatsapp && (
              <a
                href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm
                           dark:border-slate-600 dark:text-slate-300"
              >
                WhatsApp
              </a>
            )}
          </>
        )}
        {lat != null && lng != null && (
          /*
           * ★ 收货那一侧的导航按钮**高亮**。
           *
           *   这一屏上有四五个长得一样的边框按钮(打电话、WhatsApp、
           *   两侧的导航),而司机在路上要按的几乎永远是「送到哪」那个 ——
           *   他戴着手套、在阳光下、可能还在等红灯。
           *   让它一眼可辨,比让他一个个读过去要紧。
           *
           *   发货那一侧保持描边:取货阶段他多半已经知道店在哪,
           *   而且真正要点的是下面那个「确认取货」。
           */
          <button
            onClick={() => onNavigate(lat, lng, `${role} ${name}`)}
            className={
              primary
                ? 'rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm'
                : 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600 dark:text-slate-300'
            }
          >
            <InlineIcon icon={Navigation} /> 导航
          </button>
        )}
      </div>
    </section>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-slate-500">{label}</span>
      <span
        className={`text-right ${
          strong
            ? 'font-semibold text-amber-700 dark:text-amber-400'
            : 'text-slate-900 dark:text-slate-100'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function NavSheet({ options, onClose }: { options: NavOption[]; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl bg-white p-4 pb-safe dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-600" />
        <p className="mb-2 text-sm text-slate-500">用哪个导航?</p>
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => {
              navPreference.set(o.key);
              launch(o);
              onClose();
            }}
            className="mb-2 h-12 w-full rounded-xl border border-slate-300 text-left px-4
                       dark:border-slate-600 dark:text-slate-200"
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = {
  PENDING: '待领取',
  ASSIGNED: '待接单',
  ACCEPTED: '待取货',
  PICKED_UP: '配送中',
  IN_TRANSIT: '配送中',
  DELIVERED: '已送达',
  FAILED: '未送达',
  RETURNED: '已退回',
  CANCELLED: '已取消',
};

/**
 * 这一单要当心的东西 —— 代收货款、特殊物品。
 *
 * ★ 为什么值得做成会动的一条:司机一天跑几十单,详情页长得都一样。
 *   「这一单要收 120 块现金」和「这一箱是冻品」都属于**错过就当场造成损失**、
 *   而且事后补不回来的事(钱要不回来、货化了)。
 *   摆成和「重量 2.5 kg」一样的一行,他扫一眼就翻过去了。
 *
 * ★ 动画克制:只有边框在呼吸,文字不闪。
 *   整块闪的话,一天看几十次会烦到被无视 —— 那就白做了。
 *   `motion-reduce` 下直接不动(系统设置里关了动效的人多半是有原因的)。
 */
function Alerts({
  codAmount,
  codPending,
  currency,
  specialItems,
}: {
  codAmount: string;
  /** 要收钱,但收多少还没定 —— 出发前问商家 */
  codPending?: boolean;
  currency: string;
  specialItems: Array<{ code: string; label: string; icon: string | null }>;
}) {
  const cod = Number(codAmount) > 0 || codPending === true;
  if (!cod && specialItems.length === 0) return null;

  return (
    <div aria-label="特别提醒" className="space-y-2">
      {cod && (
        <div
          className="pulse-red rounded-xl border-2 border-red-400 bg-red-50 px-4 py-3
                     dark:border-red-700 dark:bg-red-950/50"
        >
          <div className="text-sm text-red-700 dark:text-red-300"><InlineIcon icon={Banknote} /> 这一单要收钱</div>
          {/*
            ★ 金额待定时**不显示 0**,写「联系商家确认」。
              显示 0 的话这块红框说的是「要收钱」、数字说的是「不用收」——
              司机信哪个都不对,而他多半信那个数字。
          */}
          <div className="text-2xl font-bold text-red-700 dark:text-red-300">
            {codPending ? '联系商家确认' : money(codAmount, currency)}
          </div>
          {/*
            ★ 这一行只做一件事:**提醒别忘了收**。
              不讲流程(「送达时要在系统里确认一次」),也不讲后果
              (「交出去就要不回来了」)—— 司机在门口扫一眼的功夫,
              读完一句话就不错了,而那一句该是要他做的那个动作。
          */}
          <div className="text-xs font-medium text-red-600 dark:text-red-400">
            {codPending ? '别忘了收钱 —— 收多少先问商家' : '别忘了收钱'}
          </div>
        </div>
      )}

      {specialItems.length > 0 && (
        <div
          className="pulse-amber rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3
                     dark:border-amber-700 dark:bg-amber-950/50"
        >
          <div className="text-sm text-amber-800 dark:text-amber-300">⚠ 这一单有特殊物品</div>
          <div className="mt-1 flex flex-wrap gap-2">
            {specialItems.map((i) => (
              <span
                key={i.code}
                className="rounded-full bg-amber-200 px-3 py-1 text-base font-semibold
                           text-amber-900 dark:bg-amber-500/25 dark:text-amber-200"
              >
                {i.icon ? `${i.icon} ` : ''}
                {i.label}
              </span>
            ))}
          </div>
          <div className="text-xs text-amber-700/80 dark:text-amber-400/80">
            送达时要逐项确认交给了收件人
          </div>
        </div>
      )}
    </div>
  );
}
